export type Platform =
	| 'polymarket'
	| 'kalshi'
	| 'opinionlabs'
	| 'predictfun'
	| 'limitless'
	| 'myriad'
	| 'hyperliquid'
export const ALL_PLATFORMS = [
	'polymarket',
	'kalshi',
	'opinionlabs',
	'predictfun',
	'limitless',
	'myriad',
	'hyperliquid',
] as const satisfies readonly Platform[]
export const DEFAULT_PLATFORMS: Platform[] = [
	'kalshi',
	'polymarket',
	'opinionlabs',
	'predictfun',
	'limitless',
	'myriad',
	'hyperliquid',
]

/**
 * Canonical UI display order for platform pickers (PlatformSelector,
 * BridgeModal, etc.). Limitless intentionally sits at the bottom — recurring
 * 15-min binary markets and pre-TGE feeds are unfamiliar to most users, so we
 * keep the more conventional venues (Polymarket / Kalshi / Predict.fun /
 * Myriad) above it. Use `sortPlatformsForDisplay` when the source array order
 * is not authored (e.g. derived from upstream API response).
 */
export const PLATFORM_DISPLAY_ORDER: Platform[] = [
	'polymarket',
	'kalshi',
	'predictfun',
	'opinionlabs',
	'myriad',
	'hyperliquid',
	'limitless',
]

export function sortPlatformsForDisplay<T extends Platform>(platforms: readonly T[]): T[] {
	const rank = new Map(PLATFORM_DISPLAY_ORDER.map((p, i) => [p, i]))
	const fallbackRank = PLATFORM_DISPLAY_ORDER.length
	return [...platforms].sort(
		(a, b) => (rank.get(a) ?? fallbackRank) - (rank.get(b) ?? fallbackRank)
	)
}

// Unified market status
// - active: Market is open for trading
// - closed: Market is not active (closed, resolved, paused, etc.)
export type MarketStatus = 'active' | 'closed'
export type Category =
	| 'politics'
	| 'sports'
	| 'crypto'
	| 'economy'
	| 'entertainment'
	| 'science'
	| 'other'
	| 'live-crypto'
export const ALL_CATEGORIES = [
	'politics',
	'sports',
	'crypto',
	'economy',
	'entertainment',
	'science',
	'other',
	'live-crypto',
] as const satisfies readonly Category[]

// Event list item (shown in sidebar, table, and tiles)
export type EventListItem = {
	id: string
	factoryId?: string
	platform: Platform
	title: string
	rules?: string
	subtitle?: string
	imageUrl?: string
	category: Category
	endDate?: number // Unix time
	volume: number // USD aggregated
	volume24h: number // USD last 24h
	liquidity: number // USD
	marketCount: number // number of markets in event
	// Markets for tile view (sorted by volume, top N)
	markets?: Array<{
		id: string
		title: string
		yesPrice: number
		// Custom yes/no side labels (e.g. ['San Antonio','New York'] or
		// ['Change','No Change']); undefined for plain Yes/No markets.
		outcomeLabels?: [string, string]
		status?: MarketStatus
		// Minimum trading reference needed to lazily fetch price history
		// for platforms where the market `id` alone is insufficient.
		// Kalshi/Limitless/Myriad/PredictFun don't need this.
		tradingRef?:
			| { platform: 'polymarket'; yesTokenId: string }
			| { platform: 'opinionlabs'; yesPos: string }
	}>
	// True if any market in this event is not tradable onchain
	hasUntradable?: boolean
	// True if ALL markets in this event are not active (closed/resolved)
	isInactive?: boolean
	// Winner name for resolved events (e.g. team name or outcome)
	winner?: string
}

// Individual team/participant info for sports match headers
export type TeamInfo = {
	name: string
	abbreviation: string
	logo?: string
	color?: string
	record?: string
}

// Sports-specific data for events with spreads, totals, etc.
export type SportsData = {
	gameId?: string
	score?: string
	period?: string
	elapsed?: string
	live?: boolean
	ended?: boolean
	teamAbbreviations?: Record<string, string>
	teamOrder?: string[] // ordered team names (home first) for correct display ordering
	teamsInfo?: TeamInfo[] // full team info (logo, color, record) — only available for team sports
	startTime?: string // ISO 8601 game start time
	leagueIcon?: string // league logo URL
	marketsByType: Record<string, MarketInEvent[]>
	marketTypes: string[]
}

// Event detail (shown in main panel)
export type EventDetail = Omit<EventListItem, 'markets'> & {
	markets: MarketInEvent[]
	settlementSources: Array<{ name: string; url: string }>
	seriesTicker: string
	series?: any[]
	// Optional sports data (spreads, totals, BTTS, etc.) — separate from main markets
	sportsData?: SportsData
	// Event start time (ISO 8601) — used for crypto binary past results
	startTime?: string
	// Optional crypto binary trading data (e.g. BTC Up or Down hourly)
	cryptoBinaryData?: CryptoBinaryData
}

// Market within an event
export type MarketInEvent = {
	id: string
	ticker: string
	title: string
	subtitle: string
	yesPrice: number
	noPrice: number
	rules?: string
	yesBid: number | null
	yesAsk: number | null
	noBid: number | null
	noAsk: number | null
	volume: number
	openInterest: number
	closeTime: number
	status: MarketStatus
	// Whether onchain trading is available
	// - Kalshi: false if market accounts are not initialized (redirect to kalshi.com)
	// - Polymarket: always true (all markets tradable onchain)
	isTradable: boolean
	// Whether this is a new/featured market (Polymarket only)
	isNew?: boolean
	// Whether this market is archived (Polymarket only - hidden from main feeds)
	isArchived?: boolean
	// Sports-specific fields (only populated for sports markets)
	sportsMarketType?: string // e.g. 'moneyline', 'spread', 'total', 'both_teams_to_score'
	line?: number // spread/total line value
	outcomeLabels?: [string, string] // custom labels for yes/no outcomes (e.g. ['Strickland', 'Hernandez'])
	result?: string
	eventStartTime?: any
	/** Sub-market image (e.g. team logo for sports group markets) */
	imageUrl?: string
	tradingData:
		| KalshiTradingData
		| PolymarketTradingData
		| OpinionLabsTradingData
		| PredictFunTradingData
		| LimitlessTradingData
		| MyriadTradingData
		| HyperliquidTradingData
	/**
	 * Crypto-binary discriminator copied down from the parent event when the
	 * event normalizer detects a Lumy / recurring up-or-down series. Lets
	 * row-level consumers (TradingWidget button labels, pull-down panel
	 * controls) read the structured flag instead of regexing the title.
	 */
	cryptoBinaryData?: CryptoBinaryData
}

// Individual market for flat list view
export type MarketListItem = {
	id: string
	platform: Platform
	title: string
	subtitle?: string
	imageUrl?: string
	category: Category
	yesPrice: number // 0-1 scale
	noPrice: number // 0-1 scale
	volume: number // USD
	volume24h: number // USD last 24h
	liquidity: number // USD
	openInterest: number // USD
	closeTime: number // unix ms
	status: MarketStatus
	// Whether onchain trading is available
	// - Kalshi: false if market accounts are not initialized (redirect to kalshi.com)
	// - Polymarket: always true (all markets tradable onchain)
	isTradable: boolean
	// Whether this is a new/featured market (Polymarket only)
	isNew?: boolean
	// Whether this market is archived (Polymarket only - hidden from main feeds)
	isArchived?: boolean
	// Whether this is a live crypto event (shows animated live badge)
	isLive?: boolean
	eventId: string // parent event id
	eventTitle: string // parent event title
	parentEvent?: {
		parentEventSlug: string | null
		parentEventId: string | null
	}
}

export type KalshiTradingData = {
	platform: 'kalshi'
	ticker: string
	eventTicker: string
	accounts: Record<
		string,
		{
			marketLedger: string
			yesMint: string
			noMint: string
		}
	>
}

export type PolymarketFeeSchedule = {
	rate: number // e.g., 0.072 (7.2%)
	exponent: number // typically 1
	takerOnly: boolean
}

export type PolymarketTradingData = {
	platform: 'polymarket'
	conditionId: string
	clobTokenIds: [string, string] // [yes, no]
	marketMakerAddress: string
	negRisk?: boolean // Whether this market uses negRisk contracts
	/**
	 * Whether fees are enabled for this market.
	 * When true, use feeSchedule to calculate fees.
	 */
	feesEnabled?: boolean
	/**
	 * Fee schedule from Polymarket API.
	 * Fee formula: fee = shares × rate × price × (1 - price)
	 */
	feeSchedule?: PolymarketFeeSchedule
}

export type OpinionLabsTradingData = {
	platform: 'opinionlabs'
	topicId: number
	questionId: string
	chainId: string
	currencyAddress: string
	yesPos: string // Token ID for YES position (used for price history API)
	noPos: string // Token ID for NO position
}

// Platform-agnostic crypto binary trading data (e.g. BTC Up or Down hourly)
export type CryptoBinaryData = {
	platform: 'polymarket' | 'myriad' | 'limitless'
	// Asset symbol. Format is per-platform — Polymarket uses pair form
	// ("btcusdt"), Limitless uses uppercase asset symbol ("BTC"). Hooks
	// branch on `platform` to interpret correctly.
	symbol: string
	recurrence: string // "5min" | "15min" | "hourly" | "4hour" | "daily"
	seriesSlug: string // canonical series id; polymarket: series[0].slug; limitless: stableSlug
	eventSlug: string // current instance slug
	eventStartTime?: string // ISO 8601, populated by limitless normalizer
	expirationTimestamp?: number // ms epoch, populated by limitless normalizer
}

export type PredictFunTradingData = {
	platform: 'predictfun'
	marketId: number
	conditionId: string
	outcomeIds: [string, string] // [yes, no] onChainIds
	isNegRisk: boolean
	isYieldBearing: boolean
	feeRateBps: number
}

export type LimitlessTradingData = {
	platform: 'limitless'
	slug: string
	tradeType: string // 'amm' | 'clob' | 'group'
	tokenIds: [string, string] // [yes, no]
	venue?: { exchange: string; adapter: string | null }
	conditionId?: string
	negRiskMarketId?: string
	collateralToken: { address: string; decimals: number; symbol: string }
	ammAddress?: string
	/** Pyth oracle symbol for price chart (e.g. "Crypto.BTC/USD") */
	oracleSymbol?: string
}

export type MyriadTradingMode = 'amm' | 'clob'

export type MyriadTradingData = {
	platform: 'myriad'
	marketId: number
	slug: string
	networkId: number
	outcomeId: number
	complementOutcomeId: number
	token: {
		address: string
		symbol: string
		decimals: number
	}
	predictionMarketContract: string

	/**
	 * Discriminator between Myriad's two trading flows:
	 *   - 'amm'  — PancakeSwap-style calldata via useMyriadTrading
	 *   - 'clob' — Order-book via useMyriadOrder (limit orders, EIP-712 sign)
	 *
	 * Source: normalizer detects `executionMode > 0` on the raw market.
	 * Defaults to 'amm' when missing — preserves existing AMM behaviour
	 * for every market in the API today.
	 */
	tradingMode: MyriadTradingMode

	/**
	 * MyriadCTFExchange contract address this market is settled against,
	 * populated only when tradingMode === 'clob'. The address is the same
	 * for all CLOB markets on BSC, but normalising it here means consumers
	 * read one field instead of importing the constant separately.
	 */
	exchangeContract?: `0x${string}`
}

export type HyperliquidTradingData = {
	platform: 'hyperliquid'
	outcome: number
	yesAssetId: number
	noAssetId: number
	yesCoin: `#${number}`
	noCoin: `#${number}`
	marketClass: 'priceBinary' | 'priceBucket' | 'other'
	underlying?: string
	expiry?: number
	targetPrice?: number
	priceThresholds?: number[]
	period?: string
	questionId?: number
}
